// @givmo/board — the organization's conflict register, and the officer's door into it.
//
// ══ THE REGISTER IS A READ, NOT A TABLE — AND IT IS RENDERED AS ONE ════════════════════════════
//
// There is no register table on the backend and there is no register model here. The register IS the
// organization's disclosure records, read back with their chain status derived from the version pointers.
// That matters for what this page may do: it renders what the read returned, in the order it returned
// it, with each entry's own sentence. It does not compose a register, does not summarise one, and does
// not conclude anything from one.
//
// **THE REGISTER NEVER CONCLUDES.** No entry is scored, ranked, flagged, or coloured by seriousness;
// there is no "N active conflicts" figure anywhere on this page, and no derived total of any kind. An
// entry cites its own sources — the record it is, the record it replaced, the meeting it was made at —
// and each of those is a link to the thing itself rather than an assertion about it.
//
// ══ ONE DOOR, AND WHY THE GENERIC RECORD ROUTE IS NOT IT ═══════════════════════════════════════
//
// A disclosure IS a `board_record` of kind `disclosure`, but `create_draft` and `import_record` both
// REFUSE that kind with a 400 naming this surface. Two reasons, and the second is the one that binds a
// client author: the generic route has no field for the seat, so it would answer a well-formed request
// with a raw database CHECK violation; and the register is DEFINED as every disclosure record of the
// organization, so a second creation path is a way for one to exist outside it — a register with a hole is
// worse than no register, because it reads as complete.
//
// So this page's dialog posts to `…/conflict-disclosures` and nowhere else, and the records surface
// offers no way to create one. `provenance_note` is required on this door because the officer is
// recording an act somebody else performed: the record names the DIRECTOR'S OWN SEAT as its approver,
// and without the note the entry could be read as the director having acted inside their own session.
//
// ══ THE YEAR'S ANNUAL DISCLOSURES (CBG-YR2-WEB1), DARK UNTIL SERVED ════════════════════════════
//
// Where the backend serves the annual conflict-of-interest cycle, the page has two parts, as tabs
// carried in the address (`?tab=register` · `?tab=annual`, Governance's precedent): the Register,
// now grouped by person with each person's annual answers in it (`coi-register.tsx`), and Annual
// disclosures (`coi-annual-part.tsx`). Where it doesn't — `BOARD_COI_CYCLE_ENABLED` off, the cycle
// list's route miss — the page is exactly what it was before the year existed.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowRight } from 'lucide-react';
import { Link, useNavigate, useSearch } from '../../board-routing';

import {
  isGivmoError,
  type BoardConflictDisclosureOnBehalfWrite,
  type BoardConflictDisclosureStatus,
  type OrgBoardMemberOut,
} from '@givmo/api-client';
import {
  Badge,
  Button,
  Card,
  CardBody,
  Dialog,
  ErrorMessage,
  Icon,
  Select,
  Skeleton,
  Tabs,
  TextField,
  Textarea,
  toast,
  type TabsValue,
} from '@givmo/design-system';
import { FieldNote } from '../../components/field-note';
import { PageShell } from '../../components/page-shell';
import { RegisteredDisclaimer } from '../../components/registered-disclaimer';
import { displayDate, todayAsDateInput } from '../../lib/display';
import { hasBoardPermission, useBoardHost } from '../../board-host';
import { extractBoardMembers, sortSeats } from '../roster/roster-decode';
import { seatPickerLabel } from '../in-meeting/in-meeting-decode';
import {
  disclosureRecordedLine,
  disclosureStatusLabel,
  disclosureStatusTone,
  extractDisclosures,
  type ConflictDisclosureRow,
} from './conflicts-decode';
import { useBoardOperations } from '../../board-runtime';
import { BOARD_PAGES, CONFLICTS_TABS, type ConflictsTab } from '../../board-routes';
import { AnnualPart } from './coi-annual-part';
import { shortDay, openCycleOf } from './coi-decode';
import { useCoiCycle, useCoiCycles } from './coi-queries';
import { GroupedRegister } from './coi-register';
import { coiWords as coi } from './coi-words';
/** The two filters, both the BACKEND's. Neither narrows a list this page already holds. */
const STATUS_OPTIONS = [
  { value: 'all', label: 'Everything recorded' },
  // A5c's word, as each entry's status reads it (CBG-UX-COPY2's fold, F3).
  { value: 'active', label: 'Current' },
  { value: 'superseded', label: 'Replaced by a later one' },
];

export function ConflictsRegisterPage() {
  const operations = useBoardOperations();
  const { permissions, orgRef, isReady } = useBoardHost();
  const org = orgRef ?? '';
  const enabled = isReady && orgRef !== null;
  // Recording a named director's disclosure on their behalf is the organization's act (the backend's
  // `board_conflict_disclosure:manage`), so a reader without it is offered no control at all.
  const canRecord = hasBoardPermission(permissions, 'board_conflict_disclosure:manage');

  const [statusChoice, setStatus] = useState<'all' | BoardConflictDisclosureStatus | null>(null);
  const [seatFilter, setSeatFilter] = useState<string | null>(null);
  const [recording, setRecording] = useState(false);

  // CBG-YR2-WEB1: whether the backend serves the year's annual disclosures here. `null` is its route
  // miss (the module or `BOARD_COI_CYCLE_ENABLED` off): the page is then exactly today's.
  const cyclesQuery = useCoiCycles(orgRef, enabled);
  const cycles = cyclesQuery.data ?? null;
  const served = cycles !== null;
  const deciding = enabled && cyclesQuery.isPending;
  const search = useSearch();
  const navigate = useNavigate();
  const tab: ConflictsTab =
    served && search.tab === CONFLICTS_TABS.annual
      ? CONFLICTS_TABS.annual
      : CONFLICTS_TABS.register;
  const openSummary = served ? openCycleOf(cycles) : null;
  const openCycle = useCoiCycle(served ? orgRef : null, openSummary?.id ?? null).data ?? null;
  const { orgName } = useBoardHost();
  // The organization's name where the host has none (approved, CBG-YR2-COPY1).
  const organization = orgName?.trim() || 'Your organization';
  // The grouped register opens on what stands today (o8-register); today's page on everything.
  const status = statusChoice ?? (served ? 'active' : 'all');

  const rosterQuery = useQuery({
    ...operations.officerListBoardMembersOptions({ path: { org_ref: org } }),
    enabled,
    select: (body: unknown) => sortSeats(extractBoardMembers(body)),
  });
  const roster = rosterQuery.data ?? [];

  // ⚠ THE FILTERS ARE IN THE QUERY KEY AND IN THE REQUEST, not in a client-side `.filter()`. `status`
  // is derived server-side from the version pointers and has no client-side equivalent — asking for
  // `active` is asking the backend which disclosures stand today, which is not a question this app can
  // answer from rows it happens to hold.
  const registerOptions = operations.officerListBoardConflictRegisterOptions({
    path: { org_ref: org },
    query: {
      // `undefined` keeps an unselected filter OFF the wire. A present empty/null value is not the
      // same request decision, even when a serializer happens to omit it today.
      board_member_id: seatFilter ?? undefined,
      disclosure_status: status === 'all' ? undefined : status,
    },
  });

  const registerQuery = useQuery({
    ...registerOptions,
    enabled: enabled && !deciding,
    retry: false,
    // Generated types describe the contract; this runtime decoder keeps hostile bytes from blanking
    // a volunteer-facing record surface. `select` leaves the cached generated response untouched.
    select: extractDisclosures,
  });

  const entries = registerQuery.data ?? [];
  const names = new Map(roster.map((seat) => [seat.id, seat.display_name]));
  const selectTab = (value: TabsValue) =>
    void navigate({
      to: BOARD_PAGES.conflicts,
      search: {
        tab: value === CONFLICTS_TABS.annual ? CONFLICTS_TABS.annual : CONFLICTS_TABS.register,
      },
      replace: true,
    });

  const filters = (
    <>
      <div className="min-w-[220px] flex-1">
        <Select
          label="Show"
          appearance="saas"
          value={status}
          onValueChange={(next) => {
            if (next === 'all' || next === 'active' || next === 'superseded') setStatus(next);
          }}
          options={STATUS_OPTIONS}
          testId="conflicts-filter-status"
        />
      </div>
      <div className="min-w-[220px] flex-1">
        <Select
          label="Whose"
          appearance="saas"
          placeholder={served ? coi.everyone : 'Every seat'}
          value={seatFilter}
          onValueChange={(next) => setSeatFilter(next)}
          options={roster.map((seat) => ({ value: seat.id, label: seatPickerLabel(seat) }))}
          testId="conflicts-filter-seat"
        />
      </div>
    </>
  );

  const list =
    deciding || (registerQuery.isPending && enabled) ? (
      <div className="flex flex-col gap-12" data-testid="conflicts-loading">
        <Skeleton className="h-48 w-full rounded-lg" />
        <Skeleton className="h-48 w-full rounded-lg" />
      </div>
    ) : registerQuery.isError ? (
      <Card testId="conflicts-error">
        <CardBody className="flex flex-col items-start gap-12">
          <p className="font-sans text-s14 font-w400 tracking-givmo text-text-secondary">
            We couldn’t load the register.
          </p>
          <Button
            appearance="saas"
            tone="secondary"
            onClick={() => void registerQuery.refetch()}
            testId="conflicts-error-retry"
          >
            Try again
          </Button>
        </CardBody>
      </Card>
    ) : entries.length === 0 && (!served || openCycle === null || seatFilter !== null) ? (
      <Card testId="conflicts-empty">
        <CardBody>
          <p className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
            {/* States what the BOOK holds, never what is true of the board. */}
            Nothing has been recorded here yet under these filters.
          </p>
        </CardBody>
      </Card>
    ) : served ? (
      <GroupedRegister
        entries={entries}
        seats={roster}
        cycle={openCycle}
        filtered={seatFilter !== null || status === 'superseded'}
      />
    ) : (
      <ul className="flex flex-col gap-12" data-testid="conflicts-list">
        {entries.map((entry) => (
          <RegisterEntry key={entry.record_id} entry={entry} names={names} />
        ))}
      </ul>
    );

  const dialog = recording ? (
    <RecordDisclosureDialog
      orgRef={org}
      roster={roster}
      register={entries}
      onClose={() => setRecording(false)}
      onRecorded={() => setRecording(false)}
    />
  ) : null;

  if (!served) {
    return (
      <PageShell
        title="Conflicts of interest"
        description="What your directors have told the board about their interests, and when."
        actions={
          // Recording on a director's behalf is `board_conflict_disclosure:manage`: without it, no
          // button.
          canRecord ? (
            <Button
              appearance="saas"
              tone="primary"
              disabled={!enabled}
              onClick={() => setRecording(true)}
              testId="conflicts-record"
            >
              Record a disclosure
            </Button>
          ) : undefined
        }
        testId="page-conflicts"
      >
        <RegisteredDisclaimer
          surface="BOARD_SELF_REPORTED"
          orgRef={orgRef}
          testId="conflicts-page-registered-disclaimer"
        />

        <Card testId="conflicts-filters">
          <CardBody className="flex flex-wrap gap-12">{filters}</CardBody>
        </Card>

        {list}
        {dialog}
      </PageShell>
    );
  }

  return (
    <PageShell
      title="Conflicts of interest"
      description={coi.pageDescription}
      actions={
        canRecord && tab === CONFLICTS_TABS.register ? (
          <Button
            appearance="saas"
            tone="secondary"
            disabled={!enabled}
            onClick={() => setRecording(true)}
            testId="conflicts-record"
          >
            Record a disclosure
          </Button>
        ) : undefined
      }
      testId="page-conflicts"
    >
      <Tabs.Root appearance="saas" value={tab} onValueChange={selectTab} testId="conflicts-tabs">
        <Tabs.List aria-label="Conflicts of interest">
          <Tabs.Tab value={CONFLICTS_TABS.register} testId="conflicts-tab-register">
            {coi.registerTab}
          </Tabs.Tab>
          <Tabs.Tab value={CONFLICTS_TABS.annual} testId="conflicts-tab-annual">
            {coi.annualTab}
          </Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value={CONFLICTS_TABS.register} focusable={false}>
          <div className="flex flex-col gap-16 pt-8" data-testid="coi-register">
            {openSummary !== null ? (
              <div
                className="flex flex-wrap items-center justify-between gap-8 rounded-md border border-border-subtle bg-surface px-16 py-12"
                data-testid="coi-register-year-line"
              >
                <span className="font-sans text-s14 font-w400 tracking-givmo text-text-secondary">
                  {coi.registerYearLine(
                    openSummary.label,
                    openSummary.submitted_count,
                    openSummary.people_count,
                    shortDay(openSummary.due_on),
                  )}
                </span>
                <button
                  type="button"
                  className="inline-flex items-center gap-4 font-sans text-s13 font-w600 tracking-givmo text-brand-600 outline-none hover:text-brand-700 focus-visible:underline"
                  onClick={() => selectTab(CONFLICTS_TABS.annual)}
                  data-testid="coi-register-open-year"
                >
                  {coi.seeWhosSubmitted}
                  <Icon icon={ArrowRight} size={14} aria-hidden />
                </button>
              </div>
            ) : null}
            <RegisteredDisclaimer
              surface="BOARD_SELF_REPORTED"
              orgRef={orgRef}
              testId="conflicts-page-registered-disclaimer"
            />
            <div
              className="flex flex-wrap gap-12 [&>*]:sm:max-w-[240px]"
              data-testid="conflicts-filters"
            >
              {filters}
            </div>
            {list}
          </div>
        </Tabs.Panel>
        <Tabs.Panel value={CONFLICTS_TABS.annual} focusable={false}>
          <AnnualPart
            orgRef={org}
            cycles={cycles}
            canManage={canRecord}
            organization={organization}
          />
        </Tabs.Panel>
      </Tabs.Root>
      {dialog}
    </PageShell>
  );
}

function RegisterEntry({
  entry,
  names,
}: {
  entry: ConflictDisclosureRow;
  names: Map<string, string>;
}) {
  const who = entry.disclosing_member_name ?? names.get(entry.disclosing_board_member_id) ?? null;

  return (
    <li data-testid={`conflict-disclosure-${entry.record_id}`}>
      <Card>
        <CardBody className="flex flex-col gap-10">
          <div className="flex flex-wrap items-center gap-8">
            <Badge tone={disclosureStatusTone(entry.status)}>
              {disclosureStatusLabel(entry.status)}
            </Badge>
            {who !== null ? (
              <span className="font-sans text-s14 font-w600 tracking-givmo text-text-strong">
                {who}
              </span>
            ) : null}
            {entry.disclosed_on !== null ? (
              <span className="font-sans text-s12 font-w400 tracking-givmo text-text-tertiary">
                disclosed {displayDate(entry.disclosed_on)}
              </span>
            ) : null}
          </div>

          <span className="font-sans text-s14 font-w600 tracking-givmo text-text-strong">
            {entry.title}
          </span>

          {/* The director's own words, whitespace preserved — a real disclosure is sometimes a
              paragraph, and reflowing it changes the record a reader is looking at. */}
          <p className="whitespace-pre-wrap font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary">
            {entry.interest_summary}
          </p>

          {entry.counterparty_name !== null ? (
            <span className="font-sans text-s12 font-w400 tracking-givmo text-text-secondary">
              The other party: {entry.counterparty_name}
            </span>
          ) : null}

          {/* ⚠ THE BACKEND'S SENTENCE, VERBATIM — the entry's own account of itself. */}
          <p
            className="font-sans text-s12 font-w400 leading-h15 tracking-givmo text-text-tertiary"
            data-testid={`conflict-disclosure-statement-${entry.record_id}`}
          >
            {entry.statement}
          </p>

          {/* ══ THE CITATIONS ══ Each is a LINK to the record itself, never a claim about it. A
              pointer this reader may not follow arrives absent from the backend rather than as an id
              that answers 404, so anything rendered here is something they can open. */}
          <div className="flex flex-wrap items-center gap-10">
            <CitationLink
              recordId={entry.record_id}
              label="Open the record"
              testId={`conflict-cite-self-${entry.record_id}`}
            />
            {entry.supersedes_record_id !== null ? (
              <CitationLink
                recordId={entry.supersedes_record_id}
                label="The disclosure this replaced"
                testId={`conflict-cite-supersedes-${entry.record_id}`}
              />
            ) : null}
            {entry.superseded_by_record_id !== null ? (
              <CitationLink
                recordId={entry.superseded_by_record_id}
                label="The disclosure that replaced this"
                testId={`conflict-cite-superseded-by-${entry.record_id}`}
              />
            ) : null}
            {entry.corrects_record_id !== null ? (
              <CitationLink
                recordId={entry.corrects_record_id}
                label="The record this corrects"
                testId={`conflict-cite-corrects-${entry.record_id}`}
              />
            ) : null}
            {entry.meeting_record_id !== null ? (
              <CitationLink
                recordId={entry.meeting_record_id}
                label="The meeting it was made at"
                testId={`conflict-cite-meeting-${entry.record_id}`}
              />
            ) : null}
          </div>

          <span className="font-sans text-s12 font-w400 tracking-givmo text-text-tertiary">
            {/* `recorded_at` is when the ENTRY was made — an instant with no meeting necessarily
                behind it, so the reader's own zone is the right one for it. A disclosure is not
                always made at a meeting; there is no organization calendar to take it in. */}
            {disclosureRecordedLine(entry.recorded_at, entry.capture_method)}
          </span>
        </CardBody>
      </Card>
    </li>
  );
}

function CitationLink({
  recordId,
  label,
  testId,
}: {
  recordId: string;
  label: string;
  testId: string;
}) {
  return (
    <Link
      to="/records/$recordId"
      params={{ recordId }}
      className="font-sans text-s12 font-w600 tracking-givmo text-brand-600 outline-none transition-colors hover:text-brand-700 focus-visible:underline"
      data-testid={testId}
    >
      {label}
    </Link>
  );
}

/** Which relationship this entry bears to an earlier one. They are different statements, never both. */
type LineageMode = 'none' | 'supersedes' | 'corrects';

function RecordDisclosureDialog({
  orgRef,
  roster,
  register,
  onClose,
  onRecorded,
}: {
  orgRef: string;
  roster: readonly OrgBoardMemberOut[];
  register: readonly ConflictDisclosureRow[];
  onClose: () => void;
  onRecorded: () => void;
}) {
  const operations = useBoardOperations();
  const queryClient = useQueryClient();
  const [seatId, setSeatId] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [interest, setInterest] = useState('');
  const [counterparty, setCounterparty] = useState('');
  const [disclosedOn, setDisclosedOn] = useState(todayAsDateInput());
  const [provenance, setProvenance] = useState('');
  const [lineage, setLineage] = useState<LineageMode>('none');
  const [earlierRecordId, setEarlierRecordId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Only the chosen seat's own earlier disclosures can be replaced or corrected — a supersession is a
  // statement about ONE director's position over time.
  const earlierOptions = register.filter(
    (entry) => seatId !== null && entry.disclosing_board_member_id === seatId,
  );

  const save = useMutation({
    mutationFn: async () => {
      if (seatId === null) return;
      const body: BoardConflictDisclosureOnBehalfWrite = {
        interest_summary: interest.trim(),
        title: title.trim() || null,
        counterparty_name: counterparty.trim() || null,
        // Required (CBG-FIX6): a disclosure is filed as the record at once, and a record is made
        // only with its date — the save stays withdrawn until there is one.
        disclosed_on: disclosedOn.trim(),
        provenance_note: provenance.trim(),
        // Exactly one lineage pointer, or neither. The backend refuses both together; the mode picker
        // above makes that a choice the officer makes rather than a 422 they have to decode.
        supersedes_record_id: lineage === 'supersedes' ? earlierRecordId : null,
        corrects_record_id: lineage === 'corrects' ? earlierRecordId : null,
      };
      await operations.officerRecordBoardConflictDisclosure({
        path: { org_ref: orgRef },
        // ⚠ The seat is a QUERY parameter on the generated operation, never a body field.
        query: { board_member_id: seatId },
        body,
        throwOnError: true,
      });
    },
    onSuccess: async () => {
      toast.success('Disclosure recorded.');
      // Every filter combination is its own cache entry. The generated key without a `query` member
      // is the family prefix for this organization, so all of its filtered register reads refresh.
      await queryClient.invalidateQueries({
        queryKey: operations.officerListBoardConflictRegisterQueryKey({
          path: { org_ref: orgRef },
        }),
      });
      onRecorded();
    },
    onError: (caught) => {
      setError(isGivmoError(caught) ? caught.message : 'We couldn’t save that. Please try again.');
    },
  });

  const ready =
    seatId !== null &&
    interest.trim().length > 0 &&
    disclosedOn.trim().length > 0 &&
    provenance.trim().length > 0 &&
    (lineage === 'none' || earlierRecordId !== null);

  return (
    <Dialog.Root open onOpenChange={(next) => !next && !save.isPending && onClose()}>
      <Dialog.Content
        aria-label="Record a disclosure"
        className="flex max-h-[80vh] flex-col gap-16 overflow-y-auto"
        testId="disclosure-dialog"
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          Record a disclosure
        </Dialog.Title>

        <p className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary">
          {/* Says whose act it is and whose record it becomes — the whole point of the on-behalf door.
              CBG-FIX104 (row 3.2, approved 2026-10-17): the list offers committee members who
              aren't on the board and officers who aren't directors, and board links are gone. */}
          For a disclosure someone made outside this portal — on a signed form, in an email, or in
          the room. The entry names their seat, not your account. Someone serving on the board can
          also record their own when they sign in.
        </p>

        <Select
          label="Who disclosed"
          appearance="saas"
          placeholder="Choose a seat"
          value={seatId}
          onValueChange={(next) => {
            setSeatId(next);
            // The earlier-record picker is scoped to the seat, so a seat change invalidates a choice
            // made against the previous one.
            setEarlierRecordId(null);
          }}
          options={roster.map((seat) => ({ value: seat.id, label: seatPickerLabel(seat) }))}
          testId="disclosure-seat"
        />

        <TextField
          label="A short title (optional)"
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          testId="disclosure-title"
        />

        <Textarea
          label="The interest, in their words"
          value={interest}
          onChange={(event) => setInterest(event.target.value)}
          rows={4}
          testId="disclosure-interest"
        />

        <TextField
          label="The other party (optional)"
          value={counterparty}
          onChange={(event) => setCounterparty(event.target.value)}
          testId="disclosure-counterparty"
        />
        <FieldNote testId="disclosure-counterparty-note">
          {/* Says exactly what naming it buys, and — just as importantly — what its absence costs, so
              a blank field is a choice rather than an accident. */}
          Naming the person or organization on the other side is what lets this disclosure be put in
          front of you beside an agenda item or motion that mentions them. Where the interest is the
          director’s own — their compensation, say — there is no other party to name, and you can
          link it to an item yourself.
        </FieldNote>

        <TextField
          label="Date disclosed"
          type="date"
          value={disclosedOn}
          onChange={(event) => setDisclosedOn(event.target.value)}
          testId="disclosure-date"
        />
        <FieldNote testId="disclosure-date-note">
          Required. The day the director made it, which may not be today — the ten-year keeping
          period is counted from it.
        </FieldNote>

        <Textarea
          label="Where this came from"
          value={provenance}
          onChange={(event) => setProvenance(event.target.value)}
          rows={2}
          testId="disclosure-provenance"
        />
        <FieldNote testId="disclosure-provenance-note">
          Required. A signed form dated 3 June, an email, a statement made in the room — so this
          entry is never read as the director having typed it themselves.
        </FieldNote>

        <Select
          label="Does this relate to an earlier disclosure?"
          appearance="saas"
          value={lineage}
          onValueChange={(next) => {
            if (next === null) return;
            setLineage(next as LineageMode);
            setEarlierRecordId(null);
          }}
          options={[
            { value: 'none', label: 'No — this stands on its own' },
            { value: 'supersedes', label: 'Their position has changed since an earlier one' },
            { value: 'corrects', label: 'An earlier record was wrong' },
          ]}
          testId="disclosure-lineage"
        />
        <FieldNote testId="disclosure-lineage-note">
          {/* The distinction the backend enforces, stated in the terms a secretary thinks in. */}
          These say different things. A changed position leaves the earlier disclosure standing as
          an accurate record of what was true at its time; a correction says it was wrong.
        </FieldNote>

        {lineage !== 'none' ? (
          <Select
            label="Which earlier disclosure"
            appearance="saas"
            placeholder="Choose one"
            value={earlierRecordId}
            onValueChange={(next) => setEarlierRecordId(next)}
            options={earlierOptions.map((entry) => ({
              value: entry.record_id,
              label: `${entry.title} — ${displayDate(entry.disclosed_on)}`,
            }))}
            testId="disclosure-earlier"
          />
        ) : null}

        {error !== null ? <ErrorMessage testId="disclosure-error">{error}</ErrorMessage> : null}

        <div className="flex justify-end gap-10">
          <Button
            appearance="saas"
            tone="secondary"
            disabled={save.isPending}
            onClick={onClose}
            testId="disclosure-cancel"
          >
            Cancel
          </Button>
          <Button
            appearance="saas"
            tone="primary"
            loading={save.isPending}
            disabled={!ready || save.isPending}
            onClick={() => {
              setError(null);
              save.mutate();
            }}
            testId="disclosure-save"
          >
            Record it
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}
